import $ from 'jquery';
import 'jquery-ui/dist/jquery-ui';
import 'jquery-ui/themes/base/base.css';
import React, { ReactElement, useEffect, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import './DatePickerField.scss';
import { isDateOnlyEqual } from '../../../utils/DateUtils';
import { Fieldset } from "@volkswagen-onehub/components-core";

const StyledContainer = styled.div`
  display: flex;

  & > .vw-ui-datepicker {
    flex-grow: 1;
  }
`;

export interface DatePickerFieldProps {
  name: string;
  availableDates?: Date[];
  // Bounds of the booking window. They keep the calendar on the month the window starts
  // in — without them it opens on today, which shows an empty grid whenever the window
  // has already rolled over into the next month — and stop the customer from paging out
  // of the window into months that hold no slots at all.
  minDate?: Date;
  maxDate?: Date;

  onChange?(value: Date): void;
}

export function DatePickerField({ name, onChange, availableDates, minDate, maxDate }: DatePickerFieldProps): ReactElement {
  const { control } = useFormContext();
  const {
    field: { value, onChange: formOnChange },
  } = useController({
    name,
    control,
  });
  const ref = useRef<HTMLDivElement>(null);
  const datePickerInitialized = useRef(false);

  const availableDatesRef = useRef(availableDates);
  const valueRef = useRef(value);

  useEffect(() => {
    availableDatesRef.current = availableDates;
  }, [availableDates]);

  useEffect(() => {
    valueRef.current = value;

    if (datePickerInitialized.current && ref.current) {
      const $datepicker = $(ref.current);
      $datepicker.datepicker('refresh');
    }
  }, [value]);

  useEffect(() => {
    if (datePickerInitialized.current || !ref.current) return;

    const $datepicker = $(ref.current);

    $datepicker.datepicker({
      defaultDate: value || minDate || new Date(),
      minDate: minDate ?? null,
      maxDate: maxDate ?? null,
      showOtherMonths: true,
      selectOtherMonths: false,
      onSelect: function (dateText: string) {
        const selectedDate = new Date(dateText);

        formOnChange(selectedDate);
        if (onChange) {
          onChange(selectedDate);
        }
      },
      beforeShowDay: function (date: Date) {
        const isAvailable = availableDatesRef.current?.some((x) => isDateOnlyEqual(x, date));
        const isSelected = valueRef.current && isDateOnlyEqual(date, valueRef.current);

        const cssClass = isSelected ? 'ui-datepicker-selected-day' : '';

        return [isAvailable ?? false, cssClass];
      },
    });

    const calendarDiv = $datepicker.find('.ui-datepicker-inline');
    calendarDiv.removeClass((_, className): string => {
      const classes = className.match(/[^ ]*-ui-datepicker$/) || [];
      return classes.join(' ');
    });
    calendarDiv.addClass('vw-ui-datepicker');

    datePickerInitialized.current = true;

    return () => {
        $datepicker.datepicker('destroy');
        datePickerInitialized.current = false;
    };
  }, []);

  useEffect(() => {
    if (!datePickerInitialized.current || !ref.current || !availableDates) return;

    const $datepicker = $(ref.current);
    $datepicker.datepicker('refresh');
  }, [availableDates]);

  // The window moves when the day rolls over, and jQuery UI redraws the month it is
  // allowed to show as the bounds change.
  useEffect(() => {
    if (!datePickerInitialized.current || !ref.current) return;

    const $datepicker = $(ref.current);
    $datepicker.datepicker('option', { minDate: minDate ?? null, maxDate: maxDate ?? null });
  }, [minDate, maxDate]);

  return (
    <Fieldset>
      <StyledContainer ref={ref} className="vw-ui-datepicker" />
    </Fieldset>
  );
}
